import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import ClientCard from '../components/ClientCard'
import { formatMoneyShort } from '../lib/format'
import Users from './Users'
import type { Client, NamedRef } from '../types'

type Tab =
  | 'clients'
  | 'subclients'
  | 'projects'
  | 'sources'
  | 'products'
  | 'services'
  | 'tariffs'
  | 'employees'

export default function Catalogs() {
  const { can } = useAuth()
  const [tab, setTab] = useState<Tab>('clients')

  return (
    <>
      <h1 className="page-title">Справочники</h1>
      <div className="tabs">
        <button className={tab === 'clients' ? 'active' : ''} onClick={() => setTab('clients')}>
          Клиенты
        </button>
        <button
          className={tab === 'subclients' ? 'active' : ''}
          onClick={() => setTab('subclients')}
        >
          Подклиенты
        </button>
        <button className={tab === 'projects' ? 'active' : ''} onClick={() => setTab('projects')}>
          Проекты
        </button>
        <button className={tab === 'sources' ? 'active' : ''} onClick={() => setTab('sources')}>
          Источники
        </button>
        <button className={tab === 'products' ? 'active' : ''} onClick={() => setTab('products')}>
          Продукты
        </button>
        <button className={tab === 'services' ? 'active' : ''} onClick={() => setTab('services')}>
          Сервисы кабинетов
        </button>
        <button className={tab === 'tariffs' ? 'active' : ''} onClick={() => setTab('tariffs')}>
          Тарифы
        </button>
        {can('users.manage') && (
          <button
            className={tab === 'employees' ? 'active' : ''}
            onClick={() => setTab('employees')}
          >
            Сотрудники
          </button>
        )}
      </div>

      {tab === 'clients' && <Clients />}
      {tab === 'subclients' && <Subclients />}
      {tab === 'projects' && <Projects />}
      {tab === 'sources' && <Sources />}
      {tab === 'products' && (
        <NamedCatalog
          title="Продукты"
          hint="Выпадающий список «Продукт» в доступах к базам и облаку."
          placeholder="1С:Розница"
          queryKey="products"
          load={() => api.products(true)}
          create={(name) => api.createProduct(name)}
          update={(id, body) => api.updateProduct(id, body)}
          remove={(id) => api.deleteProduct(id)}
        />
      )}
      {tab === 'tariffs' && <Tariffs />}
      {tab === 'employees' && can('users.manage') && <Users />}
      {tab === 'services' && (
        <NamedCatalog
          title="Сервисы личных кабинетов"
          hint="Выпадающий список «Название сервиса» в личных кабинетах."
          placeholder="Честный знак"
          queryKey="portal-services"
          load={() => api.portalServices(true)}
          create={(name) => api.createPortalService(name)}
          update={(id, body) => api.updatePortalService(id, body)}
          remove={(id) => api.deletePortalService(id)}
        />
      )}
    </>
  )
}

function useError() {
  const [error, setError] = useState('')
  const node = error ? (
    <div className="error" onClick={() => setError('')}>
      {error} (нажмите, чтобы скрыть)
    </div>
  ) : null
  return { node, show: (e: any) => setError(e?.message ?? 'Ошибка') }
}

/* ---------------- клиенты ---------------- */

function Clients() {
  const qc = useQueryClient()
  /* Права раздельные: заводить, править и удалять клиентов — три разных
     галочки в группе доступа. Раньше здесь стоял общий признак «администратор»,
     и выданное специалисту право ничего не меняло: сервер его пускал, а кнопок
     на экране не было. */
  const { can } = useAuth()
  const mayCreate = can('clients.create')
  const mayEdit = can('clients.edit')
  const mayDelete = can('clients.delete')
  const err = useError()
  const [name, setName] = useState('')
  const [sourceId, setSourceId] = useState('')
  const [card, setCard] = useState<{ client: Client; tab: 'card' | 'contacts' } | null>(null)

  const clients = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => api.clients({ include_inactive: true }),
  })

  /* Связанные клиенты видно сразу: головная организация, под ней с отступом
     её компании. Самостоятельные идут обычными строками в общем порядке. */
  const ordered = useMemo(() => {
    const all = clients.data ?? []
    const byParent = new Map<number, Client[]>()
    for (const c of all) {
      if (c.parent_id) {
        byParent.set(c.parent_id, [...(byParent.get(c.parent_id) ?? []), c])
      }
    }
    const out: { client: Client; child: boolean }[] = []
    for (const c of all) {
      if (c.parent_id) continue // дочерние выводятся под своей головной
      out.push({ client: c, child: false })
      for (const kid of byParent.get(c.id) ?? []) out.push({ client: kid, child: true })
    }
    // клиент, чья головная организация отключена и не попала в список
    const shown = new Set(out.map((r) => r.client.id))
    for (const c of all) if (!shown.has(c.id)) out.push({ client: c, child: true })
    return out
  }, [clients.data])
  const sources = useQuery({ queryKey: ['sources'], queryFn: () => api.sources() })

  const refresh = () => qc.invalidateQueries({ queryKey: ['clients'] })

  const create = useMutation({
    mutationFn: () =>
      api.createClient({ name, source_id: sourceId ? Number(sourceId) : null }),
    onSuccess: () => {
      setName('')
      refresh()
    },
    onError: err.show,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateClient(id, body),
    onSuccess: refresh,
    onError: err.show,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteClient(id),
    onSuccess: () => qc.invalidateQueries(),
    onError: err.show,
  })

  return (
    <>
      {err.node}
      {mayCreate && (
        <div className="toolbar">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Название клиента</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="ООО Опыт" />
          </div>
          <div className="field">
            <label>Источник по умолчанию</label>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              <option value="">—</option>
              {sources.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <button className="btn primary" disabled={!name.trim()} onClick={() => create.mutate()}>
            Добавить
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Головная организация</th>
              <th>Источник по умолчанию</th>
              <th>Ведутся проекты</th>
              <th>Активен</th>
              <th>Карточка</th>
              {mayDelete && <th />}
            </tr>
          </thead>
          <tbody>
            {ordered.map(({ client: c, child }) => (
              <tr key={c.id} className={child ? 'group-child' : undefined}>
                <td>
                  {child && <span className="group-branch">└</span>}
                  {/* Название открывает карточку: переименование и остальные
                      сведения правятся там, а не строкой в таблице. */}
                  <button
                    className="link-btn"
                    onClick={() => setCard({ client: c, tab: 'card' })}
                    title="Открыть карточку клиента"
                  >
                    <span className={c.children_count > 0 ? 'group-head' : undefined}>{c.name}</span>
                  </button>
                  {c.children_count > 0 && (
                    <span className="group-badge">группа · {c.children_count}</span>
                  )}
                </td>
                <td title="Группа компаний: клиенты остаются раздельными, связь только для отчётности">
                  {mayEdit ? (
                    <select
                      value={c.parent_id ?? ''}
                      onChange={(e) =>
                        update.mutate({
                          id: c.id,
                          body: { parent_id: e.target.value ? Number(e.target.value) : null },
                        })
                      }
                    >
                      <option value="">— самостоятельный —</option>
                      {clients.data
                        ?.filter((x) => x.id !== c.id)
                        .map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    (c.parent_name ?? '—')
                  )}
                </td>
                <td>
                  {mayEdit ? (
                    <select
                      value={c.source_id ?? ''}
                      onChange={(e) =>
                        update.mutate({
                          id: c.id,
                          body: { source_id: e.target.value ? Number(e.target.value) : null },
                        })
                      }
                    >
                      <option value="">—</option>
                      {sources.data?.map((src) => (
                        <option key={src.id} value={src.id}>
                          {src.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (c.source?.name ?? '—')
                  )}
                </td>
                <td title="Пока снято, поле «Проект» в задачах этого клиента не показывается">
                  {mayEdit ? (
                    <input
                      type="checkbox"
                      checked={c.has_projects}
                      onChange={(e) =>
                        update.mutate({ id: c.id, body: { has_projects: e.target.checked } })
                      }
                    />
                  ) : c.has_projects ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                <td>
                  {mayEdit ? (
                    <input
                      type="checkbox"
                      checked={c.is_active}
                      onChange={(e) =>
                        update.mutate({ id: c.id, body: { is_active: e.target.checked } })
                      }
                    />
                  ) : c.is_active ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                <td>
                  <button className="btn small" onClick={() => setCard({ client: c, tab: 'contacts' })}>
                    Контакты {c.contacts_count || ''} · Доступы {c.accesses_count || ''}
                  </button>
                </td>
                {mayDelete && (
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить клиента «${c.name}» вместе с его объектами и проектами?`))
                          remove.mutate(c.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {card && (
        <ClientCard
          clientId={card.client.id}
          clientName={card.client.name}
          tab={card.tab}
          onClose={() => setCard(null)}
        />
      )}
    </>
  )
}

/* ---------------- подклиенты ---------------- */

function Subclients() {
  const qc = useQueryClient()
  /* Источники, продукты, объекты и проекты сервер закрывает одним правом
     «Вести справочники» — им же закрываем и кнопки. */
  const { can } = useAuth()
  const mayManage = can('catalogs.manage')
  const err = useError()
  const [clientId, setClientId] = useState('')
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const subclients = useQuery({
    queryKey: ['subclients', clientId ? Number(clientId) : undefined],
    queryFn: () => api.subclients(clientId ? Number(clientId) : undefined),
  })

  const create = useMutation({
    mutationFn: () =>
      api.createSubclient({ client_id: Number(clientId), name, address: address || null }),
    onSuccess: () => {
      setName('')
      setAddress('')
      qc.invalidateQueries({ queryKey: ['subclients'] })
    },
    onError: err.show,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteSubclient(id),
    onSuccess: () => qc.invalidateQueries(),
    onError: err.show,
  })

  const clientName = (id: number) => clients.data?.find((c) => c.id === id)?.name ?? '—'

  return (
    <>
      {err.node}
      <div className="toolbar">
        <div className="field">
          <label>Фильтр по клиенту</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Все клиенты</option>
            {clients.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {mayManage && (
          <>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label>Название объекта</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Магазин на Ленина"
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 180 }}>
              <label>Адрес</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <button
              className="btn primary"
              disabled={!clientId || !name.trim()}
              onClick={() => create.mutate()}
            >
              Добавить
            </button>
          </>
        )}
      </div>
      {mayManage && !clientId && (
        <p style={{ color: 'var(--muted)', marginTop: -6 }}>
          Чтобы добавить объект, выберите клиента в фильтре.
        </p>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Объект</th>
              <th>Адрес</th>
              <th>Контакт</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {subclients.data?.length === 0 && (
              <tr>
                <td colSpan={mayManage ? 5 : 4} className="empty">
                  Подклиентов нет
                </td>
              </tr>
            )}
            {subclients.data?.map((s) => (
              <tr key={s.id}>
                <td>{clientName(s.client_id)}</td>
                <td>{s.name}</td>
                <td>{s.address ?? ''}</td>
                <td>{s.contact_person ?? ''}</td>
                {mayManage && (
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить объект «${s.name}»?`)) remove.mutate(s.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/* ---------------- проекты ---------------- */

function Projects() {
  const qc = useQueryClient()
  /* Источники, продукты, объекты и проекты сервер закрывает одним правом
     «Вести справочники» — им же закрываем и кнопки. */
  const { can } = useAuth()
  const mayManage = can('catalogs.manage')
  const err = useError()
  const [clientId, setClientId] = useState('')
  const [subclientId, setSubclientId] = useState('')
  const [name, setName] = useState('')

  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.clients() })
  const subclients = useQuery({
    queryKey: ['subclients', clientId ? Number(clientId) : undefined],
    queryFn: () => api.subclients(clientId ? Number(clientId) : undefined),
    enabled: !!clientId,
  })
  const projects = useQuery({
    queryKey: ['projects', clientId ? Number(clientId) : undefined],
    queryFn: () => api.projects(clientId ? { client_id: Number(clientId) } : {}),
  })

  const create = useMutation({
    mutationFn: () =>
      api.createProject({
        client_id: Number(clientId),
        subclient_id: subclientId ? Number(subclientId) : null,
        name,
      }),
    onSuccess: () => {
      setName('')
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: err.show,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteProject(id),
    onSuccess: () => qc.invalidateQueries(),
    onError: err.show,
  })

  const clientName = (id: number) => clients.data?.find((c) => c.id === id)?.name ?? '—'

  return (
    <>
      {err.node}
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Проект доступен в задаче, только если в карточке клиента отмечено «Ведутся проекты».
        Признак включается автоматически при создании первого проекта клиента.
      </p>
      <div className="toolbar">
        <div className="field">
          <label>Клиент</label>
          <select
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value)
              setSubclientId('')
            }}
          >
            <option value="">Все клиенты</option>
            {clients.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        {mayManage && (
          <>
            <div className="field">
              <label>Объект (необязательно)</label>
              <select
                value={subclientId}
                onChange={(e) => setSubclientId(e.target.value)}
                disabled={!clientId}
              >
                <option value="">—</option>
                {subclients.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ flex: 1, minWidth: 200 }}>
              <label>Название проекта</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Внедрение Клеверенс"
              />
            </div>
            <button
              className="btn primary"
              disabled={!clientId || !name.trim()}
              onClick={() => create.mutate()}
            >
              Добавить
            </button>
          </>
        )}
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Клиент</th>
              <th>Проект</th>
              <th>Объект</th>
              <th>Сроки</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {projects.data?.length === 0 && (
              <tr>
                <td colSpan={mayManage ? 5 : 4} className="empty">
                  Проектов нет
                </td>
              </tr>
            )}
            {projects.data?.map((p) => (
              <tr key={p.id}>
                <td>{clientName(p.client_id)}</td>
                <td>{p.name}</td>
                <td>{subclients.data?.find((s) => s.id === p.subclient_id)?.name ?? '—'}</td>
                <td>
                  {p.starts_on ?? '—'} … {p.ends_on ?? '—'}
                </td>
                {mayManage && (
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить проект «${p.name}»?`)) remove.mutate(p.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/* ---------------- источники ---------------- */

function Sources() {
  const qc = useQueryClient()
  /* Источники, продукты, объекты и проекты сервер закрывает одним правом
     «Вести справочники» — им же закрываем и кнопки. */
  const { can } = useAuth()
  const mayManage = can('catalogs.manage')
  const err = useError()
  const [name, setName] = useState('')

  const sources = useQuery({ queryKey: ['sources', 'all'], queryFn: () => api.sources(true) })
  const refresh = () => qc.invalidateQueries({ queryKey: ['sources'] })

  const create = useMutation({
    mutationFn: () => api.createSource({ name }),
    onSuccess: () => {
      setName('')
      refresh()
    },
    onError: err.show,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateSource(id, body),
    onSuccess: refresh,
    onError: err.show,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteSource(id),
    onSuccess: () => qc.invalidateQueries(),
    onError: err.show,
  })

  return (
    <>
      {err.node}
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Источник — кто привёл работу: партнёр, сайт, повторное обращение. Указывается у каждой
        задачи, по нему ведётся отдельный расчёт. У клиента можно задать источник по умолчанию —
        он подставится в новую задачу.
      </p>

      {mayManage && (
        <div className="toolbar">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Новый источник</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Станислав"
            />
          </div>
          <button className="btn primary" disabled={!name.trim()} onClick={() => create.mutate()}>
            Добавить
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Источник</th>
              <th>Описание</th>
              <th>Активен</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {sources.data?.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td className="wrap">{s.description ?? ''}</td>
                <td>
                  {mayManage ? (
                    <input
                      type="checkbox"
                      checked={s.is_active}
                      onChange={(e) =>
                        update.mutate({ id: s.id, body: { is_active: e.target.checked } })
                      }
                    />
                  ) : s.is_active ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                {mayManage && (
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить источник «${s.name}»?`)) remove.mutate(s.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}


/* ---------------- продукты и сервисы кабинетов ---------------- */

interface NamedCatalogProps {
  title: string
  hint: string
  placeholder: string
  queryKey: string
  load: () => Promise<NamedRef[]>
  create: (name: string) => Promise<NamedRef>
  update: (id: number, body: Record<string, unknown>) => Promise<NamedRef>
  remove: (id: number) => Promise<void>
}

/** Плоский справочник «название + активность». Ведёт только администратор. */
function NamedCatalog({
  title,
  hint,
  placeholder,
  queryKey,
  load,
  create,
  update,
  remove,
}: NamedCatalogProps) {
  const qc = useQueryClient()
  /* Источники, продукты, объекты и проекты сервер закрывает одним правом
     «Вести справочники» — им же закрываем и кнопки. */
  const { can } = useAuth()
  const mayManage = can('catalogs.manage')
  const err = useError()
  const [name, setName] = useState('')

  const items = useQuery({ queryKey: [queryKey, 'all'], queryFn: load })
  const refresh = () => qc.invalidateQueries({ queryKey: [queryKey] })

  const add = useMutation({
    mutationFn: () => create(name.trim()),
    onSuccess: () => {
      setName('')
      refresh()
    },
    onError: err.show,
  })

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => update(id, body),
    onSuccess: refresh,
    onError: err.show,
  })

  const drop = useMutation({
    mutationFn: (id: number) => remove(id),
    onSuccess: refresh,
    onError: err.show,
  })

  return (
    <>
      {err.node}
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>{hint}</p>

      {mayManage && (
        <div className="toolbar">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label>Добавить в справочник</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={placeholder}
            />
          </div>
          <button className="btn primary" disabled={!name.trim()} onClick={() => add.mutate()}>
            Добавить
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{title}</th>
              <th>Активен</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {items.data?.length === 0 && (
              <tr>
                <td colSpan={3} className="empty">
                  Справочник пуст
                </td>
              </tr>
            )}
            {items.data?.map((it) => (
              <tr key={it.id}>
                <td>
                  {mayManage ? (
                    <input
                      defaultValue={it.name}
                      style={{ width: '100%' }}
                      onBlur={(e) => {
                        const v = e.target.value.trim()
                        if (v && v !== it.name) patch.mutate({ id: it.id, body: { name: v } })
                      }}
                    />
                  ) : (
                    it.name
                  )}
                </td>
                <td>
                  {mayManage ? (
                    <input
                      type="checkbox"
                      checked={it.is_active}
                      onChange={(e) =>
                        patch.mutate({ id: it.id, body: { is_active: e.target.checked } })
                      }
                    />
                  ) : it.is_active ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                {mayManage && (
                  <td>
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить «${it.name}» из справочника?`)) drop.mutate(it.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * Справочник тарифов.
 *
 * Держим у себя, а не тянем из 1С: обмен — отдельная большая работа, а
 * клиентов десятки. Прикрутим обмен, когда понадобится.
 */
function Tariffs() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const mayManage = can('catalogs.manage')
  const err = useError()
  const [form, setForm] = useState({ name: '', price: '', hours: '' })

  const tariffs = useQuery({
    queryKey: ['tariffs', 'all'],
    queryFn: () => api.tariffs(true),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['tariffs'] })

  const create = useMutation({
    mutationFn: () =>
      api.createTariff({
        name: form.name.trim(),
        price_per_month: Number(form.price) || 0,
        included_hours: form.hours ? Number(form.hours) : null,
      }),
    onSuccess: () => {
      setForm({ name: '', price: '', hours: '' })
      refresh()
    },
    onError: err.show,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.updateTariff(id, body),
    onSuccess: refresh,
    onError: err.show,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteTariff(id),
    onSuccess: refresh,
    onError: err.show,
  })

  return (
    <>
      {err.node}
      {mayManage && (
        <div className="toolbar">
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label>Название тарифа</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Базовый"
            />
          </div>
          <div className="field" style={{ maxWidth: 160 }}>
            <label>Цена в месяц, ₽</label>
            <input
              type="number"
              min="0"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
          </div>
          <div className="field" style={{ maxWidth: 160 }}>
            <label>Включено часов</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              placeholder="не про часы"
            />
          </div>
          <button
            className="btn primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Добавить
          </button>
        </div>
      )}

      <p className="hint">
        Включённые часы расходуются задачами с отметкой «Сопровождение». Оставьте
        поле пустым, если тариф не про часы — тогда клиенту расход не показывается.
      </p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Название</th>
              <th className="num">Цена в месяц</th>
              <th className="num">Включено часов</th>
              <th className="num">Клиентов</th>
              <th>Активен</th>
              {mayManage && <th />}
            </tr>
          </thead>
          <tbody>
            {tariffs.data?.length === 0 && (
              <tr>
                <td colSpan={mayManage ? 6 : 5} className="empty">
                  Тарифов пока нет
                </td>
              </tr>
            )}
            {tariffs.data?.map((t) => (
              <tr key={t.id} style={t.is_active ? undefined : { opacity: 0.6 }}>
                <td>{t.name}</td>
                <td className="num mono">{formatMoneyShort(Number(t.price_per_month))}</td>
                <td className="num mono">
                  {t.included_hours ? `${Number(t.included_hours)} ч` : '—'}
                </td>
                <td className="num mono">{t.clients_count || ''}</td>
                <td>
                  {mayManage ? (
                    <input
                      type="checkbox"
                      checked={t.is_active}
                      onChange={(e) =>
                        update.mutate({ id: t.id, body: { is_active: e.target.checked } })
                      }
                    />
                  ) : t.is_active ? (
                    'да'
                  ) : (
                    'нет'
                  )}
                </td>
                {mayManage && (
                  <td className="nowrap">
                    <button
                      className="btn small danger"
                      onClick={() => {
                        if (confirm(`Удалить тариф «${t.name}»?`)) remove.mutate(t.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
